import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Clapperboard, Film, Users, Ticket, Swords } from "lucide-react";
import api from "@/lib/api";

export const gross = (n) => (n >= 1e9 ? `$${(n / 1e9).toFixed(2)}bn` : `$${Math.round(n / 1e6)}m`);
const premiere = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

export const Poster = ({ m, className = "" }) => {
  const [broken, setBroken] = useState(false);
  const inner = m.poster && !broken
    ? <img src={m.poster} alt={`${m.title} poster`} loading="lazy" onError={() => setBroken(true)} className="w-full h-full object-cover" />
    : <div className="w-full h-full bg-ink text-gold flex flex-col items-center justify-center gap-1 p-2 text-center"><Clapperboard className="w-6 h-6" /><span className="text-[10px] font-bold uppercase tracking-wider leading-tight">{m.title}</span></div>;
  const box = <div className={`aspect-[2/3] overflow-hidden rounded-md shadow-[0_6px_18px_-6px_rgba(0,0,0,0.45)] ring-1 ring-black/10 bg-ink ${className}`}>{inner}</div>;
  return m.wiki_url ? <a href={m.wiki_url} target="_blank" rel="noopener noreferrer" title={`${m.title} on Wikipedia`} className="block shrink-0 transition-transform duration-300 hover:-translate-y-1 hover:rotate-[-1deg]">{box}</a> : <div className="shrink-0">{box}</div>;
};

export const MovieCard = ({ m, testid }) => (
  <article data-testid={testid} className="flex gap-4 bg-white border border-[#e5e7eb] rounded-xl p-4">
    <Poster m={m} className="w-[88px] sm:w-[100px]" />
    <div className="flex flex-col min-w-0 flex-1">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[22px] font-bold text-gold leading-none">{m.year}</span>
        <span className="px-2 py-0.5 rounded-full bg-ink text-gold text-[10px] font-bold uppercase tracking-wider">All-time #{m.rank}</span>
      </div>
      <h3 className="mt-2 text-[16px] font-bold text-ink leading-snug">{m.title}</h3>
      <p className="mt-0.5 text-[12px] text-ink-muted">Premiered {premiere(m.release_date)}{m.genre_primary ? ` · ${m.genre}` : ""}</p>
      {m.directors?.length > 0 && <p className="mt-2 text-[13px] text-ink flex items-start gap-1.5"><Film className="w-3.5 h-3.5 mt-0.5 text-ink-muted shrink-0" /> {m.directors.join(", ")}</p>}
      {m.cast?.length > 0 && <p className="mt-1 text-[13px] text-ink-muted flex items-start gap-1.5"><Users className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {m.cast.join(", ")}</p>}
      <div className="mt-auto pt-2 flex flex-wrap items-center justify-between gap-2 border-t border-[#f1f5f9] text-[12px] text-ink-muted">
        <span className="inline-flex items-center gap-1 font-semibold text-ink"><Ticket className="w-3.5 h-3.5 text-gold" /> {gross(m.gross)} worldwide</span>
        {m.company && <span className="truncate">{m.company}</span>}
      </div>
    </div>
  </article>
);

export const MoviesBirthYear = ({ data, compact = false, className = "" }) => {
  if (!data || data.items.length === 0) return null;
  const top = data.items[0];
  return (
    <div data-testid="movies-birth-year" className={`rounded-2xl border border-ink bg-paper ${compact ? "p-4" : "p-5"} ${className}`}>
      <div className="flex items-center gap-2">
        <Clapperboard className="w-4 h-4 text-gold" />
        <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-gold">The biggest film the year you were born · {data.year}</p>
      </div>
      {data.nearest_year && <p data-testid="movies-birth-nearest" className="mt-2 text-[12px] text-ink-muted">No {data.year} release made the all-time box-office list — the nearest giant premiered in {data.nearest_year}.</p>}
      <div className="mt-3 flex gap-4">
        <Poster m={top} className={compact ? "w-[72px]" : "w-[96px]"} />
        <div className="min-w-0 flex-1">
          <p className="text-[22px] font-bold text-gold leading-none">{top.year}</p>
          <h3 data-testid="movies-birth-title" className="mt-1 text-[17px] font-bold text-ink leading-snug">{top.title}</h3>
          <p className="mt-0.5 text-[12px] text-ink-muted">Premiered {premiere(top.release_date)}{top.genre ? ` · ${top.genre}` : ""}</p>
          {top.directors?.length > 0 && <p className="mt-2 text-[13px] text-ink flex items-start gap-1.5"><Film className="w-3.5 h-3.5 mt-0.5 text-ink-muted shrink-0" /> {top.directors.join(", ")}</p>}
          {!compact && top.cast?.length > 0 && <p className="mt-1 text-[13px] text-ink-muted flex items-start gap-1.5"><Users className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {top.cast.join(", ")}</p>}
          <p className="mt-2 text-[13px] font-semibold text-ink inline-flex items-center gap-1"><Ticket className="w-3.5 h-3.5 text-gold" /> {gross(top.gross)} worldwide · all-time #{top.rank}</p>
          {data.items.length > 1 && <p className="mt-2 text-[12px] text-ink-muted">Also that year: {data.items.slice(1).map((m) => `${m.title} (${gross(m.gross)})`).join(" · ")}</p>}
        </div>
      </div>
      <Link to={`/movies/${Math.floor(top.year / 10) * 10}s`} data-testid="movies-birth-link" className="mt-3 inline-block text-[12px] font-semibold text-gold hover:underline">The biggest films of the {Math.floor(top.year / 10) * 10}s →</Link>
    </div>
  );
};

export const MoviesBirthdayTwins = ({ data, compact = false, className = "" }) => {
  if (!data || !data.items?.length) return null;
  const [dd, mm] = data.date_dd_mm.split("-");
  const dayLabel = new Date(2000, Number(mm) - 1, Number(dd)).toLocaleDateString("en-GB", { day: "numeric", month: "long" });
  return (
    <div data-testid="movies-birthday-twins" className={`rounded-2xl border border-ink bg-paper ${compact ? "p-4" : "p-5"} ${className}`}>
      <div className="flex items-center gap-2">
        <Clapperboard className="w-4 h-4 text-gold" />
        <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-gold">Your movie birthday twins · premiered on {dayLabel}</p>
      </div>
      <p className="mt-1 text-[12px] text-ink-muted">{data.total} box-office hit{data.total === 1 ? "" : "s"} opened on your exact birthday.</p>
      <div className={`mt-3 grid gap-3 ${compact ? "grid-cols-3" : "grid-cols-3 sm:grid-cols-6"}`}>
        {data.items.map((m, i) => (
          <div key={m.id} data-testid={`movies-twin-${i + 1}`} className="text-center">
            <Poster m={m} className="w-full" />
            <p className="mt-1.5 text-[12px] font-bold text-ink leading-tight line-clamp-2">{m.title}</p>
            <p className="text-[11px] text-ink-muted">{m.year} · {gross(m.gross)}</p>
          </div>
        ))}
      </div>
      <Link to="/movies" data-testid="movies-twins-link" className="mt-3 inline-block text-[12px] font-semibold text-gold hover:underline">All movies →</Link>
    </div>
  );
};

export const MoviesSection = ({ ddmm, dateLabel = "today", limit = 12 }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!ddmm) return;
    api.get("/movies", { params: { date: ddmm, limit } }).then((r) => setData(r.data)).catch(() => setData({ total: 0, items: [] }));
  }, [ddmm, limit]);
  if (!data || data.total === 0) return null;
  return (
    <section id="movies" data-testid="movies-section" className="mt-10">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Clapperboard className="w-5 h-5 text-gold" />
          <h2 className="text-2xl font-bold text-ink">The Movies</h2>
          <span data-testid="movies-count" className="ml-1 text-[12px] font-semibold text-ink-muted">{data.total}</span>
        </div>
        <p className="text-[13px] text-ink-muted">Box-office giants that premiered {dateLabel}{data.total > data.items.length ? ` — showing ${data.items.length} of ${data.total}` : ""}. <Link to="/movies" data-testid="movies-all-link" className="font-semibold text-gold hover:underline">All movies →</Link> <span className="mx-1 text-ink/30">|</span> <Link to="/movies#faceoff" data-testid="movies-quiz-link" className="inline-flex items-center gap-1 font-semibold text-ink hover:text-gold"><Swords className="w-3.5 h-3.5" /> Play today's Box Office Face-Off</Link></p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="movies-grid">
        {data.items.map((m, i) => <MovieCard key={m.id} m={m} testid={`movie-card-${i + 1}`} />)}
      </div>
    </section>
  );
};
